import { BackLink, Intro } from "components/commons";
import locale from "localization";
import { StationContext } from "contexts";
import { useContext, useState } from "react";
import { FormMode, Path } from "enums";
import KeyAccountForm from "./key-account-form";
import { useApi, useModal, useRouter, useMount } from "hooks";
import { FormProvider } from "contexts/form-context";
import initialState from "./key-account-form.state";
import { createKeyAccountCollectionApi, getKeyAccountCollectionByIdApi } from "apis";
import { useMemo, useCallback } from "react";
import ViewKeyAccount from "./view-key-account";
import { combineDateAndTimeInput } from "utils";
import { formatAmount, stringToDecimal } from "utils/text.utils";
import { SuccessModal } from "components/modals";
import { AddKeyAccountSuccess } from "images";

const KeyAccountDetailsModule = () => {
  const { station } = useContext(StationContext);

  const { request: createKeyAccount, loading: isSaving } = useApi({
    api: createKeyAccountCollectionApi,
    modalError: true,
    handleOwnError: true,
    cleanObject: false,
  });
  const { request: getKeyAccountById, result: collection } = useApi({
    api: getKeyAccountCollectionByIdApi,
  });

  const [form, setForm] = useState(null);
  const confirmModal = useModal();
  const successModal = useModal();
  const router = useRouter();
  const keyAccountId = router.query?.id;
  const formMode = router.query?.mode || router.query?.pageMode || FormMode.Add;
  const isAdd = formMode === FormMode.Add;

  const handleOnSubmit = useCallback(
    async (fields) => {
      confirmModal.close();

      const payload = {
        stationCode: station.stationCode,
        shiftId: fields?.shiftId?.value || "",
        siNumber: fields?.siNumber?.value || "",
        keyAccountId: fields?.keyAccountId?.value || "",
        paymentMethod: fields?.paymentMethod?.value || "",
        amount: stringToDecimal(fields?.amount?.value) || "",
        dateTime: combineDateAndTimeInput(fields?.date.value, fields?.time.value),
        referenceNumber: fields?.referenceNumber?.value || "",
        remarks: fields?.remarks?.value || "",
      };

      const res = await createKeyAccount(payload);

      if (!res?.error) {
        successModal.show();
      }
    },
    [createKeyAccount, station.stationCode, confirmModal, successModal]
  );

  useMount(async () => {
    if (!isAdd) {
      await getKeyAccountById({ id: keyAccountId }, { useLoading: true });
    }
  });

  return (
    <>
      <BackLink text={locale.keyAccountCollectionsLogs} path={Path.KeyAccount} />
      <Intro title={isAdd ? locale.addKeyAccount : ""} />
      <FormProvider
        initialState={useMemo(() => initialState(collection), [collection])}
        onChange={(form) => setForm(form)}
        onSubmit={() => {
          confirmModal.show();
        }}
      >
        {isAdd ? (
          <KeyAccountForm loading={isSaving} collection={collection} />
        ) : (
          <ViewKeyAccount collection={collection} />
        )}
      </FormProvider>
      <SuccessModal
        {...confirmModal}
        closable={false}
        image={AddKeyAccountSuccess}
        title={`${locale.addKeyAccount}?`}
        content={locale.keyAccountCollectionConfirmMessage}
        centerButtons={true}
        contentHighlight={[formatAmount(stringToDecimal(form?.fields?.amount?.value || 0))]}
        close={() => {
          confirmModal.close();
        }}
        secondary={{
          text: locale.cancel,
          onClick: () => {
            confirmModal.close();
          },
        }}
        primary={{
          text: locale.proceed,
          onClick: () => handleOnSubmit(form?.fields),
        }}
      />
      <SuccessModal
        {...successModal}
        closable={false}
        image={AddKeyAccountSuccess}
        title={locale.keyAccountCollectionsSubmitted}
        content={locale.youHaveSuccessfullySubmittedCollection}
        centerButtons={true}
        primary={{
          text: locale.gotIt,
          onClick: () => router.push(Path.KeyAccount),
        }}
      />
    </>
  );
};

export default KeyAccountDetailsModule;
